/**
 * The Connections card on the activity feed: requests to connect with you, waiting for your answer (accept or
 * decline right here: accepting connects you both ways), and your connections. "See All" opens the Connections
 * page. Backend: GET /interact/follow-requests/summary/.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Box, Button, CardContent, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Check as AcceptIcon, Close as DeclineIcon, Email as MessageIcon, Handshake as ConnectionsIcon } from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { SUMMARY_URL, answerRequest } from './connectionsApi';
import PagedList from 'components/common/PagedList';

// people without a photo: the app's avatar colour (as on notifications and the feed), not MUI's grey
const AVATAR_SX = { bgcolor: 'primary.avatar', color: '#fff', fontWeight: 600 };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Your connections as rows like the Following card's (avatar, full name, position), five a page, sliding
 *  (components/common/PagedList.jsx). A row opens the person's profile. */
function ConnectionsCarousel({ people, onOpen, onMessage }) {
  return (
    <PagedList items={people} label="connections" renderItem={(u) => (
      <Box role="link" tabIndex={0} onClick={() => onOpen(u)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(u); }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 0.5, py: 0.25, borderRadius: 1.5, cursor: 'pointer',
          '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' } }}>
        <Avatar src={u.avatar_url || undefined} alt={u.name}
          sx={{ ...AVATAR_SX, width: 34, height: 34, fontSize: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
          {(u.name || '?')[0]}
        </Avatar>
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>{u.name}</Typography>
          {u.position && (
            <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.3 }}>{u.position}</Typography>
          )}
        </Box>
        {/* a message (the other actions would not fit here; they are on the profile and in the hub) */}
        <Tooltip title={`Message ${(u.name || '').split(' ')[0]}`}>
          <IconButton size="small" aria-label={`Message ${u.name}`} onClick={(e) => { e.stopPropagation(); onMessage(u); }}
            sx={{ flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            <MessageIcon sx={{ fontSize: '1.75rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    )} />
  );
}

export default function ConnectionsCard() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { data } = useSWR(SUMMARY_URL, fetcher);
  const [busy, setBusy] = useState(null);
  const received = data?.received || [];
  const connections = data?.connections_count || 0;

  const answer = async (req, verb) => {
    setBusy(req.id);
    try {
      await answerRequest(req.id, verb);
      showSnackbar(verb === 'accept' ? `You are now connected with ${req.user.name.split(' ')[0]}` : 'Request declined', 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'The request could not be answered.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Badge badgeContent={connections} invisible={!connections}
            // the position's transform would override MUI's way of hiding an empty badge (scale 0): hide it outright
            sx={{ '& .MuiBadge-badge': { fontSize: '.8rem', height: 16, minWidth: 16, px: 0.5, transform: 'translate(50%, -60%)', top: 0, left: 0,
              bgcolor: '#fff', color: 'primary.main', border: '1px solid', borderColor: 'primary.light', fontWeight: 600 },
              '& .MuiBadge-invisible': { display: 'none' } }}>
            <ConnectionsIcon sx={{ color: 'primary.light', fontSize: '1.45rem' }} />
          </Badge>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Connections</Typography>
        </Box>
        <Button size="small" variant="text" onClick={() => navigate('/connections/')} sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
          See All
        </Button>
      </Box>

      {/* requests to connect with you */}
      {received.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {received.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <Avatar src={r.user.avatar_url || undefined} sx={{ ...AVATAR_SX, width: 30, height: 30, fontSize: 13, cursor: 'pointer' }}
                onClick={() => navigate(`/profile/${r.user.id}/`)}>{(r.user.name || '?')[0]}</Avatar>
              <Box flex={1} minWidth={0} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${r.user.id}/`)}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>{r.user.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.3 }}>wants to connect</Typography>
              </Box>
              <Tooltip title="Accept: they see your whole profile">
                <span><IconButton size="small" disabled={busy === r.id} onClick={() => answer(r, 'accept')}
                  sx={{ bgcolor: 'success.main', color: '#fff', '&:hover': { bgcolor: 'success.dark' } }}><AcceptIcon fontSize="small" /></IconButton></span>
              </Tooltip>
              <Tooltip title="Decline">
                <span><IconButton size="small" disabled={busy === r.id} onClick={() => answer(r, 'decline')}
                  sx={{ border: 1, borderColor: 'grey.400', color: 'text.primary', ml: 0.5, '&:hover': { borderColor: 'grey.600' } }}><DeclineIcon fontSize="small" /></IconButton></span>
              </Tooltip>
            </Box>
          ))}
          {data.received_count > received.length && (
            <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => navigate('/connections/?tab=requests')}>
              {plural(data.received_count - received.length, 'more request')}
            </Button>
          )}
        </Stack>
      )}

      {!data ? null : connections === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No connections yet. Ask to connect with researchers you work with: once they accept, you see each other's whole profile.
        </Typography>
      ) : (
        <ConnectionsCarousel people={data.recent_connections || []} onOpen={(u) => navigate(`/profile/${u.id}/`)}
          onMessage={(u) => navigate(`/messages/new?user=${u.id}`)} />
      )}
    </CardContent>
  );
}
